import { FEATURE_FLAG_VALUE_TYPE } from "./feature-flag.enums";
import { z } from "zod";

/**
 * Feature flag key pattern: `mainFeature:app:description`. Exported so the
 * internal-cms admin UI and any other ad-hoc validator use the same source of
 * truth as the backend DTOs below.
 */
export const FEATURE_FLAG_KEY_REGEX = /^[a-z0-9_]+:[a-z0-9]+:[a-z0-9_]+$/;

// Value schema that accepts boolean, string, or number
export const FeatureFlagValueSchema = z.union([z.boolean(), z.string(), z.number()]);
export type FeatureFlagValue = z.infer<typeof FeatureFlagValueSchema>;

// Raw `forbiddenHubUsers` input is a comma-separated list of identifiers (defi
// addresses / emails / hub user ids). Parsed into trimmed tokens here so no
// consumer re-implements the split; `undefined` is preserved so update can tell
// "omitted" (keep existing) apart from "" (clear).
const ForbiddenHubUsersInputSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) =>
    value == null
      ? undefined
      : value
          .split(",")
          .map((token) => token.trim())
          .filter((token) => token.length > 0),
  );

export const CreateFeatureFlagDtoSchema = z.object({
  key: z
    .string()
    .min(1)
    .regex(
      FEATURE_FLAG_KEY_REGEX,
      "Key must follow pattern: mainFeature:app:description (e.g., maintenance_mode:api:enable)",
    ),
  value: FeatureFlagValueSchema,
  valueType: z.nativeEnum(FEATURE_FLAG_VALUE_TYPE),
  description: z.string().optional(),
  whitelistedAddresses: z.string().nullable().optional(),
  forbiddenHubUsers: ForbiddenHubUsersInputSchema,
});
export type CreateFeatureFlagDto = z.infer<typeof CreateFeatureFlagDtoSchema>;
export type CreateFeatureFlagDtoInput = z.input<typeof CreateFeatureFlagDtoSchema>;

export const UpdateFeatureFlagDtoSchema = z.object({
  id: z.uuid(),
  value: FeatureFlagValueSchema,
  valueType: z.nativeEnum(FEATURE_FLAG_VALUE_TYPE),
  description: z.string().optional(),
  whitelistedAddresses: z.string().nullable().optional(),
  forbiddenHubUsers: ForbiddenHubUsersInputSchema,
});
export type UpdateFeatureFlagDto = z.infer<typeof UpdateFeatureFlagDtoSchema>;
export type UpdateFeatureFlagDtoInput = z.input<typeof UpdateFeatureFlagDtoSchema>;

export const FeatureFlagDtoSchema = z.object({
  id: z.uuid(),
  key: z
    .string()
    .min(1)
    .regex(
      FEATURE_FLAG_KEY_REGEX,
      "Key must follow pattern: mainFeature:app:description (e.g., maintenance_mode:api:enable)",
    ),
  value: FeatureFlagValueSchema,
  valueType: z.nativeEnum(FEATURE_FLAG_VALUE_TYPE),
  description: z.string().nullable().optional(),
  whitelistedAddresses: z.string().nullable().optional(),
  // Stored/read shape: resolved hubUserIds.
  forbiddenHubUsers: z.array(z.string()).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type FeatureFlagDto = z.infer<typeof FeatureFlagDtoSchema>;

export const FeatureFlagDtoArraySchema = z.array(FeatureFlagDtoSchema);
export type FeatureFlagDtoArray = z.infer<typeof FeatureFlagDtoArraySchema>;

export const GetFeatureFlagsByPageQueryParamsSchema = z.object({
  page: z.coerce.number().optional(),
  size: z.coerce.number().optional(),
  search: z.string().optional(),
});
export type GetFeatureFlagsByPageQueryParamsDto = z.infer<typeof GetFeatureFlagsByPageQueryParamsSchema>;

export const DeleteFeatureFlagQueryParamsSchema = z.object({
  id: z.uuid(),
});
export type DeleteFeatureFlagQueryParamsDto = z.infer<typeof DeleteFeatureFlagQueryParamsSchema>;

/** Upper bound keeps a single request from creating thousands of flags. */
export const BULK_FEATURE_FLAG_OPERATION_LIMIT = 500;

export const BulkCreateFeatureFlagsDtoSchema = z.object({
  featureFlags: z.array(CreateFeatureFlagDtoSchema).min(1).max(BULK_FEATURE_FLAG_OPERATION_LIMIT),
});
export type BulkCreateFeatureFlagsDto = z.infer<typeof BulkCreateFeatureFlagsDtoSchema>;
export type BulkCreateFeatureFlagsDtoInput = z.input<typeof BulkCreateFeatureFlagsDtoSchema>;

export const BulkDeleteFeatureFlagsDtoSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(BULK_FEATURE_FLAG_OPERATION_LIMIT),
});
export type BulkDeleteFeatureFlagsDto = z.infer<typeof BulkDeleteFeatureFlagsDtoSchema>;

export const FeatureFlagsBulkSuccessResponseSchema = z.object({
  message: z.string(),
  count: z.number(),
});
export type FeatureFlagsBulkSuccessResponseDto = z.infer<typeof FeatureFlagsBulkSuccessResponseSchema>;

export const GetFeatureFlagsByPageResultSchema = z.object({
  data: FeatureFlagDtoArraySchema,
  total: z.number(),
  page: z.number(),
  size: z.number(),
});
export type GetFeatureFlagsByPageResultDto = z.infer<typeof GetFeatureFlagsByPageResultSchema>;

export const FeatureFlagsCmsSuccessResponseSchema = z.object({
  message: z.string(),
});
export type FeatureFlagsCmsSuccessResponseDto = z.infer<typeof FeatureFlagsCmsSuccessResponseSchema>;

// Legacy v1 hub response - flat map (backwards-compatible for old cached frontends)
export const HubFeatureFlagsLegacyResponseSchema = z.record(z.string(), FeatureFlagValueSchema);
export type HubFeatureFlagsLegacyResponseDto = z.infer<typeof HubFeatureFlagsLegacyResponseSchema>;

// Hub feature flags v2 response - structured entries with value and whitelist
export const HubFeatureFlagEntrySchema = z.object({
  value: FeatureFlagValueSchema,
  whitelistedAddresses: z.string().nullable(),
  // Resolved hubUserIds the flag is disabled for.
  forbiddenHubUsers: z.array(z.string()),
});
export type HubFeatureFlagEntry = z.infer<typeof HubFeatureFlagEntrySchema>;

export const HubFeatureFlagsResponseSchema = z.record(z.string(), HubFeatureFlagEntrySchema);
export type HubFeatureFlagsResponseDto = z.infer<typeof HubFeatureFlagsResponseSchema>;
